"use client";

import React, { useEffect, useMemo, useState } from "react";
import { signIn, signOut, useSession } from "next-auth/react";
import { ReactNode } from "react";
import * as R from "ramda";
import { useQuery } from "@tanstack/react-query";
import { COOKIE_API_URL, LOGIN_IFRAME_URL, TRACK_EVENTS } from "@/lib/constant";

type ILoginMessageEvent = {
  data: {
    userid: string;
    username: string;
    alias: string;
    gender: string;
    active: boolean;
    defaultSchool: string;
    defaultAddress: string;
    apiKey: string;
  };
  id: "loginVerify" | string;
  status: "logged";
};

const queryAuthFn = async ({ signal }: { signal: AbortSignal }) => {
  const cookie = await fetch(COOKIE_API_URL, {
    method: "GET",
    credentials: "include",
    signal,
  });
  if (cookie.status === 401) {
    return null;
  }
  const cookiejson = await cookie.json();
  const user = cookiejson?.[0]?.[0];
  if (cookie.ok && user) {
    return user;
  }
  return null;
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const { data: session, status } = useSession();
  const {
    data: user,
    refetch,
    error,
    isLoading,
    isError,
    isPending,
    isFetching,
    status: queryStatus,
  } = useQuery({
    queryKey: ["auth"],
    queryFn: queryAuthFn,
    retry: false,
    refetchInterval: 2 * 60 * 1000,
    staleTime: 0,
  });
  const [isLoginRequesting, setIsLoginRequesting] = useState(false);

  const handleLogin = async (userId: string) => {
    try {
      setIsLoginRequesting(true);
      await signIn("credentials", { redirect: false, userId });
      globalThis?.umami?.track(TRACK_EVENTS.login, { id: userId });
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoginRequesting(false);
    }
  };

  // @DEPRECATED
  useEffect(() => {
    if (status === "unauthenticated") {
      const intervalId = setInterval(refetch, 1000);
      return () => clearInterval(intervalId);
    }
  }, [refetch, status]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent<ILoginMessageEvent>) => {
      if (event.data.id === "loginVerify" && event.data.status === "logged") {
        refetch();
      }
    };
    globalThis.addEventListener("message", handleMessage);
    return () => {
      globalThis.removeEventListener("message", handleMessage);
    };
  }, [refetch]);

  useEffect(() => {
    if (isPending) {
      return;
    }
    // NOTE: fix 登录态不同步的问题
    if (status === "authenticated") {
      if (isError || user?.userid !== session?.user?.id) {
        signOut();
      }
    } else if (status === "unauthenticated") {
      const ua = navigator.userAgent.toLowerCase();
      const isWechatBrowser = ua.indexOf("micromessenger") !== -1;
      if (user) {
        handleLogin(user.userid);
      } else if (isWechatBrowser) {
        console.log('Redirecting to wechat login...')
        window.location.href = `https://beta.cloud.cocorobo.cn/#/login?url=${encodeURIComponent(
          window.location.href
        )}`;
      }
    }
  }, [isError, isPending, session?.user?.id, status, user]);

  useEffect(() => {
    // umami 标记用户
    if (status === "authenticated" && !isPending && !isFetching && !isError) {
      globalThis?.umami?.identify(session?.user);
    }
  }, [isError, status, session, isPending, isFetching]);

  const isShowAuthModal = useMemo(() => {
    // if (process.env.NODE_ENV === 'development') {
    //   return false
    // }
    if (!user && !isPending && status === "unauthenticated") {
      return true;
    }
    return false;
  }, [isPending, status, user]);

  return (
    <>
      {children}
      {isShowAuthModal ? (
        <dialog className="modal modal-open">
          <div className="modal-box relative px-0 border-none w-[95%] laptop:w-96 h-[480px]">
            <iframe className="w-full h-full" src={LOGIN_IFRAME_URL}></iframe>
            {status === "loading" || isLoginRequesting ? (
              <div className="absolute top-0 left-0 w-full h-full bg-black/50 flex items-center justify-center">
                <span className="loading loading-spinner loading-lg m-auto"></span>
              </div>
            ) : null}
          </div>
        </dialog>
      ) : null}
      {isError ? (
        <div className="toast toast-end z-10">
          <div className="alert alert-error shadow-lg">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 shrink-0 stroke-current"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <h3 className="font-bold">{"Error"}</h3>
              <div className="text-xs">{error.message}</div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
